import {computed, type ComputedRef, inject, type InjectionKey, provide, reactive} from "vue";
import get from "lodash.get";
import {
	AccessMaskingTest,
	AndMaskingTest,
	DataMaskingTest,
	MaskingTest, MaskingTestTarget,
	MaskOverride,
	MasksMaskingTest, NegateMaskingTest, OrMaskingTest,
	PeerMaskingTest, RoleMaskingTest
} from "@t/fd/layout/Masked";
import type {ListItemBase} from "@t/list/List";
import type {FormDefinition} from "@t/fd/layout/FormDefinition";
import {injectEnv, userInRole} from "@fd/Environment.ts";
import {isSecured, ObjectAccessFlags, secureRootKey} from "@t/security/ObjectAccess.ts";
import {InterfaceRoot, isFormRoot, RootLayout} from "@t/fd/InterfaceRoot.ts";

export const UNMASK_ALL = "*unmaskAll";
export const MASK_DEFAULT = "*maskDefault";


export type VisibilityModifier = "default" | "visible" | "hidden";

export interface MaskedItem extends MaskOverride {
    label?: string;
    override: VisibilityModifier;
}


export interface MaskingLib {
    overrideMask: (path: string) => void;
    maskedList: ComputedRef<ListItemBase[]>;
    evaluate: (path:string, label:string|undefined|null, localData:any, viewMode:boolean, test:MaskingTest) => boolean;
	evaluateTest: (localData:any, viewMode:boolean, test:MaskingTest) => boolean;
	joinEvaluate: (targets: MaskingTestTarget[], localData:any, viewMode:boolean) => boolean;
}

export const maskingLibKey = Symbol() as InjectionKey<MaskingLib>;

export function injectMaskingLib():MaskingLib {
    let visLib = inject(maskingLibKey);
    if (typeof visLib === "undefined") throw "Undefined visibility exception lib (not provided)";
    return visLib;
}

export default function (root:ComputedRef<InterfaceRoot<RootLayout>>, dataRoot:any):MaskingLib {
    const registry = reactive<MaskedItem[]>([]);

    function sortIn(mi: MaskedItem):void {
        try {
            if (!mi.label) {
                registry.push(mi);
                return;
            }
            for (let i = 0; i < registry.length; i++) {
                const label = registry[i].label;
                if (label == null || label.localeCompare(mi.label) > 0) {
                    registry.splice(i, 0, mi);
                    return;
                }
            }
            registry.push(mi);
        } finally {
            maskedList.value
        }
    }

    function overrideMask(path: string):void {
        if (path === UNMASK_ALL) {
            registry.forEach((ep: MaskedItem) => ep.override = "visible");
            return;
        } else if (path === MASK_DEFAULT) {
            registry.forEach((ep: MaskedItem) => {
                const ord = findOverride(ep.path);
                if (ord) ep.override = ord.masked ? "hidden" : "visible";
                else ep.override = "default"
            });
            return;
        }
        const item = registry.find((ep: MaskedItem) => ep.path === path);
        if (item) {
            item.override = "visible";
        }
    }

    function reallyMasked(item: MaskedItem):boolean {
        if (item.override == null || item.override === "default") return item.masked;
        return item.override === "hidden";
    }

    const maskedList = computed<ListItemBase[]>(() => {
        const list:ListItemBase[] = [];
        for (let item of registry) {
            if (item.label && reallyMasked(item)) list.push({ value: item.path, name: item.label });
        }
        return list;
    });

    function isFalsy(value:any):boolean {
        if (Array.isArray(value)) return value.length === 0;
        return !value;
    }

    function evaluateDataTest(localData:any, t:DataMaskingTest):boolean {
        //console.log("Testing local data " + JSON.stringify(localData)+"\n"+JSON.stringify(t));
        let p = t.d;
        let data = localData;
        if (p.indexOf('.') ===  0) {
            p = p.substring(1);
            data = dataRoot;
        }
        let value;
        //console.log("   path = " + p);
        if (p.length === 0) value = data;
        else {
            value = get(data, p);
        }
        if (typeof t.value === "undefined") {
            return isFalsy(value);
        }
        if (Array.isArray(t.value)) {
            // List of possible values
            for (let val of t.value) {
                if (value === val) return true;
            }
        }
        return value === t.value;
    }

    // returns true if there is no masked items
    function evaluateMasksTest(t:MasksMaskingTest):boolean {
        return maskedList.value.length === 0;
    }

    function evaluatePeerTest(t:PeerMaskingTest):boolean {
        const current = registry.find((ep: MaskedItem) => ep.path === t.path);
        return current ? current.masked : false;
    }

    function evaluateRoleTest(t:RoleMaskingTest):boolean {
        const env = injectEnv();
		return userInRole(env.user, t.name);
    }

    function evaluateAccessTest(t:AccessMaskingTest):boolean {
		let secroot = inject(secureRootKey, dataRoot);
		if (isSecured(secroot)) {
			const userAccess = secroot._access;
			for (let flag of ObjectAccessFlags) {
				// Does the test require checking this flag?
				if (t[flag]) {
					// Yes - the is required
					if (!userAccess[flag]) return false;	// permission is not present
				}
			}
			// All required permissions are there
			return true;
		} else return false;
    }

    function evaluateTest(localData:any, viewMode:boolean, test:MaskingTest):boolean {
        switch (test.on) {
            case "data":
                return evaluateDataTest(localData, test as DataMaskingTest);
            case "masks":
                return evaluateMasksTest(test as MasksMaskingTest);
            case "initial":
                return true;
            case "view":
                return viewMode;
            case "peer":
                return evaluatePeerTest(test as PeerMaskingTest);
			case "role":
				return evaluateRoleTest(test as RoleMaskingTest);
			case "access":
				return evaluateAccessTest(test as AccessMaskingTest);
            case "and":
                for (let ti of (test as AndMaskingTest).tests) {
                    const andResult = evaluateTest(localData, viewMode, ti);
                    if (!andResult) return false;
                }
                return true;
            case "or":
                for (let ti of (test as OrMaskingTest).tests) {
                    const orResult = evaluateTest(localData, viewMode, ti);
                    if (orResult) return true;
                }
                return false;
            case "not":
                return !evaluateTest(localData, viewMode, (test as NegateMaskingTest).test);
            default:
                throw "Unimplemented masking test '"+test.on+"'";
        }
    }

    function dumpRegistry() {
        //maskedList();
        //console.log("---\n" + JSON.stringify(registry));
    }

    function findOverride(path:string):MaskOverride|null {
        if (isFormRoot(root.value) && root.value.content.masks) {
            for (let override of root.value.content.masks) {
                if (override.path === path) return override;
            }
        }
        return null;
    }


    function evaluate(path:string, label:string|undefined|null, localData:any, viewMode:boolean, test:MaskingTest):boolean {
        const testResult = evaluateTest(localData, viewMode, test);
        let registered = registry.find((ep: MaskedItem) => ep.path === path);
        if (registered) {
            registered.masked = testResult;
        } else {
            const hasLabel = typeof label === "string";
            registered = {
                path: path,
                label: hasLabel ? label : undefined,
                masked: testResult,
                override: "default"
            };
            const ord = findOverride(path);
            if (ord) {
                registered.override = ord.masked ? "hidden" : "visible";
            }
            sortIn(registered);
        }
        return reallyMasked(registered);
    }

	function joinEvaluate(targets: MaskingTestTarget[], localData:any, viewMode:boolean):boolean {
		let allMasked = true;
		for (let target of targets) {
			allMasked = allMasked && evaluateTest(localData, viewMode, target.test);
		}

		let rem = true;
		let visibleOverrides = 0;
		for (let target of targets) {
			let registered = registry.find((ep: MaskedItem) => ep.path === target.path);
			if (registered) {
				registered.masked = allMasked;
			} else {
				const hasLabel = typeof target.label === "string";
				registered = {
					path: target.path,
					label: hasLabel ? target.label : undefined,
					masked: allMasked,
					override: "default"
				};
				const ord = findOverride(target.path);
				if (ord) {
					registered.override = ord.masked ? "hidden" : "visible";
				}
				sortIn(registered);
			}
			if (registered.override === "visible") visibleOverrides++;
			rem = rem && reallyMasked(registered);
		}
		if (visibleOverrides > 0 && visibleOverrides < targets.length) {
			// one or more items were overriden (explicitly made visible) but not all
			for (let target of targets) {
				let registered = registry.find((ep: MaskedItem) => ep.path === target.path);
				registered.override = "visible";	// make them all visible
			}
		}
		return rem;
	}

    const lib:MaskingLib = {
        overrideMask,
        maskedList,
        evaluate,
		evaluateTest,
		joinEvaluate
    };

    provide(maskingLibKey, lib);
    return lib;
}
